export default class PriceCalculator {

    /**
     * Dispatches subsequent price calculations based on passed state
     * @returns {Number} Price in number form, unformatted
     */
    static calculateSubtotal() {
        if (!this.state || this.state.loading) {
            return 0;
        }

        //null safe guards.
        if (
            !this.state.itemGroup ||
            !this.state.itemGroup.items ||
            !this.state.choices ||
            this.state.choices.item === undefined ||
            !this.state.itemGroup.items[this.state.choices.item]
        ) {
            return 0;
        }
        // Get an object representing the base item
        const baseItem = this.state.itemGroup.items[this.state.choices.item];

        // Gather all chosen modifiers
        const modifiersToCalculate = {};
        // First loop through all non-included modifiers
        for (const modKey in this.state.choices.modifiers) {
            if (this.state.choices.modifiers.hasOwnProperty(modKey)) {
                const modifier = this.state.choices.modifiers[modKey];
                //Push every regular modifier to the modifiersToCalculate group
                modifiersToCalculate[modKey] = modifier;
            }
        }
        /**
         * Now loop through all included modifiers, adding the modifiers where quantity has been increase
         */
        var includedModifiersToCalculate = {};
        for (const includedKey in this.state.choices.includedModifiers) {
            if (this.state.choices.includedModifiers.hasOwnProperty(includedKey)) {
                let baseQuantity = 0;
                const includedModifierChoice = this.state.choices.includedModifiers[includedKey];
                let originalIncludedModifier = this.state.includedModifiers[includedKey];
                // If they selected extra increase baseQuantity
                if(includedModifierChoice.size === 'extra') {
                    baseQuantity++;
                }
                // Now check to see if the quantity has been increased
                const includedModifierChoiceQuantity = includedModifierChoice?.quantity ?? 1;
                const originalIncludedModifierQuantity = originalIncludedModifier?.quantity ?? 0;
                if(includedModifierChoiceQuantity > originalIncludedModifierQuantity) {
                    // We now know it has been increased, so we get the absolute value of the difference
                    let diff = Math.abs(includedModifierChoiceQuantity - originalIncludedModifierQuantity);
                    baseQuantity = baseQuantity + diff;
                }

                //Now perform a check to see if we need to add this to our calculatable mods
                if(baseQuantity > 0) {
                    includedModifiersToCalculate[includedKey] = {
                        ...includedModifierChoice,
                        quantity: baseQuantity
                    };
                }
            }
        }
        //Determine pricing strategy
        const useMatrix = baseItem.usesMatrixPricing;
        let price;
        if(useMatrix) {
            price = this.calculatePriceForMatrixPricing(baseItem, modifiersToCalculate, includedModifiersToCalculate) * this.state.quantity;
        }else {
            price = this.calculatePriceForPriceLevels(baseItem, modifiersToCalculate, includedModifiersToCalculate) * this.state.quantity;
        }
        // Now finally let's add the addOns
        let addOnChoices = this.state.choices.addOns;
        price = price + this.calculatePriceForAddons(addOnChoices);
        if (price < 0) {
            return 'Invalid Selection';
        } else {
            return price;
        }
    }

    /**
     *
     * @param {number} priceLevel Price Level of base item in calculation
     * @param {number} modifier ID of modifier to perform lookup on
     * @returns {number} Cost of the inputted modifier for the base Item's price level
     * Looks up modifier price for price level, accounting for portion adjustments
     */
    static getModifierPriceForPriceLevel(priceLevel, modifier) {
        return this.getModifierPriceForChoice(priceLevel, modifier, this.state.choices.modifiers[modifier], true);
    }

    static getIncludedModifierPriceForPriceLevel(priceLevel, modifier, choice = this.state.choices.includedModifiers[modifier]) {
        return this.getModifierPriceForChoice(priceLevel, modifier, choice, false);
    }

    static getModifierPriceForChoice(priceLevel, modifier, choice, applyExtraAdjustment) {
        if (!choice) {
            return 0;
        }

        const chosenSize = choice.size;
        const chosenPortion = choice.portion;
        let priceAdjustment = 1;
        const storeExtraAdjustmentRate = window.session.store.level_extra_adjustment_rate;
        let finalPrice = 0;
        if(chosenPortion === 'first-half' || chosenPortion === 'second-half') {
            priceAdjustment = priceAdjustment / 2;
        }
        if(applyExtraAdjustment && chosenSize === 'extra') {
            priceAdjustment = priceAdjustment * storeExtraAdjustmentRate;
        }
        if(choice.quantity > 0) {
            const modifierPrice = this.state.modifiers?.[modifier]?.priceLevels?.[priceLevel];
            if (modifierPrice === undefined || modifierPrice === null) {
                return 0;
            }
            finalPrice = modifierPrice * priceAdjustment * choice.quantity;
        }
        if(!isNaN(finalPrice)) {
            //Should catch NaNs as well
            return finalPrice
        }else {
            console.log(`ERROR! Price level: ${priceLevel} - modifier: ${modifier} returned price`);
            console.log(finalPrice);
            console.log('Modifiers object: ');
            console.log(this.state.modifiers[modifier]);
            return 0;
        }
    }

    static calculateModifierPriceForGivenItem(mainItem, mod) {
        //if the item should be calculated via matrix pricing.
        if (mainItem.usesMatrixPricing && mod.matrixWeight > 0) {
            /*
            When using matrix pricing, the modifier's matrix weight acts as a key
            to access that price in the main item's matrix price object.
             */
            //Base price is always the 0 key.
            const basePrice = mainItem.matrixPrices[`0.00`];
            const priceOfBasePlusMatrixWeight = mainItem.matrixPrices[`${mod.matrixWeight.toFixed(2)}`];
            const priceOfJustModifier = priceOfBasePlusMatrixWeight - basePrice;
            return priceOfJustModifier.toFixed(2);
        } else {
            //Use level pricing, even if 0.
            return mod.priceLevels[mainItem.level];
        }
    }

    /**
     *
     * @param {Object} baseItem Object representing base item
     * @param {Array} modifiers Array of modifiers to call getModifierPriceForPriceLevel on
     * @returns {Number} Price for item
     */
    static calculatePriceForPriceLevels(baseItem, modifiers, includedModifiers = {}) {
        const priceLevel = baseItem.level;
        let totalPrice = baseItem.basePrice;
        for (const key in modifiers) {
            if (modifiers.hasOwnProperty(key)) {
                const addedPrice = this.getModifierPriceForPriceLevel(priceLevel, key);
                totalPrice = totalPrice + addedPrice;
            }
        }
        for (const key in includedModifiers) {
            if (includedModifiers.hasOwnProperty(key)) {
                const addedPrice = this.getIncludedModifierPriceForPriceLevel(priceLevel, key, includedModifiers[key]);
                totalPrice = totalPrice + addedPrice;
            }
        }
        return totalPrice;
    }

    /**
     *
     * @param {Object} baseItem Object representing the base item
     * @param {Array} modifiers Array of modifiers to call price Calculation funcitons on
     */
    static calculatePriceForMatrixPricing(baseItem, modifiers, includedModifiers) {
        const itemMatrix = baseItem.matrixPrices;
        let totalMatrixWeight = 0;
        let priceFromLeveledModifiers = 0;
        for (const key in modifiers) {
            if (modifiers.hasOwnProperty(key)) {
                const matrixForMod = this.getTotalMatrixWeightForChosenModifier(key);
                if(matrixForMod === 0 && modifiers[key].quantity > 0) {
                    //If this item is a matrix weight of 0, instead use price level strategy
                    let priceForPriceLevel = this.getModifierPriceForPriceLevel(baseItem.level, key);
                    priceFromLeveledModifiers = priceFromLeveledModifiers + priceForPriceLevel;
                }
                totalMatrixWeight = totalMatrixWeight + matrixForMod;
            }
        }
        // Now loop through included
        for (const key in includedModifiers) {
            if (includedModifiers.hasOwnProperty(key)) {
                const matrixForMod = this.getTotalMatrixWeightForChosenIncluded(key);
                if(matrixForMod === 0 && includedModifiers[key].quantity > 0) {
                    //If this item is a matrix weight of 0, instead use price level strategy
                    let priceForPriceLevel = this.getIncludedModifierPriceForPriceLevel(baseItem.level, key, includedModifiers[key]);
                    priceFromLeveledModifiers = priceFromLeveledModifiers + priceForPriceLevel;
                }
                totalMatrixWeight = totalMatrixWeight + matrixForMod;
            }
        }
        /**
         * TODO: Make this extensible to any number of matrix weights
         */
        if(totalMatrixWeight > 10) {
            totalMatrixWeight = 10;
        }
        totalMatrixWeight = totalMatrixWeight.toFixed(2);
        let priceForMatrix = parseFloat(itemMatrix[totalMatrixWeight]);
        return priceForMatrix + priceFromLeveledModifiers;
    }

    /**
     *
     * @param {Number} modifier ID of modifier to perform calculation on
     * @returns {Number} Total matrix weight of modifier
     * Applies size and portion adjustments to determine actual weight adjustment
     */
    static getTotalMatrixWeightForChosenModifier(modifier) {
        const choice = this.state.choices.modifiers[modifier];
        if (!choice) {
            return 0;
        }

        const chosenQuantity = choice.quantity;
        const chosenSize = choice.size;
        const chosenPortion = choice.portion;
        let weightAdjustment = 1;
        const storeExtraAdjustmentRate = window.session.store.matrix_extra_adjustment_rate;
        if(chosenPortion === 'first-half' || chosenPortion === 'second-half') {
            weightAdjustment = weightAdjustment / 2;
        }
        if(chosenSize === 'extra') {
            weightAdjustment = weightAdjustment * storeExtraAdjustmentRate;
        }
        //If something is wrong with the configuration it's better to return 0 than error.
        const sourceMatrixWeight = this.state.modifiers?.[modifier]?.matrixWeight ?? 0;
        return sourceMatrixWeight * weightAdjustment * chosenQuantity;
    }

    static getTotalMatrixWeightForChosenIncluded(modifier) {
        const choice = this.state.choices.includedModifiers[modifier];
        if (!choice) {
            return 0;
        }

        const chosenQuantity = choice.quantity;
        const chosenSize = choice.size;
        const chosenPortion = choice.portion;
        let weightAdjustment = 1;
        // const storeExtraAdjustmentRate = window.session.store.matrix_extra_adjustment_rate;
        const storeExtraAdjustmentRate = 1;
        if(chosenPortion === 'first-half' || chosenPortion === 'second-half') {
            weightAdjustment = weightAdjustment / 2;
        }
        if(chosenSize === 'extra') {
            weightAdjustment = weightAdjustment * storeExtraAdjustmentRate;
        }
        // A saved choice can outlive a modifier that was removed from the menu.
        const sourceMatrixWeight = this.state.modifiers?.[modifier]?.matrixWeight ?? 0;
        return sourceMatrixWeight * weightAdjustment * chosenQuantity;
    }

    static calculatePriceForAddons(addonChoices) {
        let cost = 0;
        for (const key in addonChoices) {
            if (addonChoices.hasOwnProperty(key)) {
                const choice = addonChoices[key];
                if (!choice) {
                    continue;
                }
                let addOnCost = choice.price * choice.quantity;
                if (!isNaN(addOnCost)) {
                    cost = cost + addOnCost;
                }
            }
        }
        // Get a string representation of the rounded amount
        let fixedCost = cost.toFixed(2);
        // recast it to a number
        let finalCost = parseFloat(fixedCost);
        return finalCost;
    }

    static calculateSubtotalWithoutAddons() {
        if (!this.state || this.state.loading) {
            return 0;
        }

        //If we don't have the necessary info don't proceed.
        if (
            !this.state.itemGroup ||
            !this.state.itemGroup.items ||
            !this.state.choices ||
            this.state.choices.item === undefined ||
            !this.state.itemGroup.items[this.state.choices.item]
        ) {
            return 0;
        }
        // Get an object representing the base item
        const baseItem = this.state.itemGroup.items[this.state.choices.item];

        // Gather all chosen modifiers
        const modifiersToCalculate = {};
        // First loop through all non-included modifiers
        for (const modKey in this.state.choices.modifiers) {
            if (this.state.choices.modifiers.hasOwnProperty(modKey)) {
                const modifier = this.state.choices.modifiers[modKey];
                //Push every regular modifier to the modifiersToCalculate group
                modifiersToCalculate[modKey] = modifier;
            }
        }
        /**
         * Now loop through all included modifiers, adding the modifiers where quantity has been increase
         */
        var includedModifiersToCalculate = {};
        for (const includedKey in this.state.choices.includedModifiers) {
            if (this.state.choices.includedModifiers.hasOwnProperty(includedKey)) {
                let baseQuantity = 0;
                const includedModifierChoice = this.state.choices.includedModifiers[includedKey];
                let originalIncludedModifier = this.state.includedModifiers[includedKey];
                // If they selected extra increase baseQuantity
                if(includedModifierChoice.size === 'extra') {
                    baseQuantity++;
                }
                // Now check to see if the quantity has been increased
                const includedModifierChoiceQuantity = includedModifierChoice?.quantity ?? 1;
                const originalIncludedModifierQuantity = originalIncludedModifier?.quantity ?? 0;
                if(includedModifierChoiceQuantity > originalIncludedModifierQuantity) {
                    // We now know it has been increased, so we get the absolute value of the difference
                    let diff = Math.abs(includedModifierChoiceQuantity - originalIncludedModifierQuantity);
                    baseQuantity = baseQuantity + diff;
                }

                //Now perform a check to see if we need to add this to our calculatable mods
                if(baseQuantity > 0) {
                    includedModifiersToCalculate[includedKey] = {
                        ...includedModifierChoice,
                        quantity: baseQuantity
                    };
                }
            }
        }
        //Determine pricing strategy
        const useMatrix = baseItem.usesMatrixPricing;
        let price;
        if(useMatrix) {
            price = this.calculatePriceForMatrixPricing(baseItem, modifiersToCalculate, includedModifiersToCalculate) * this.state.quantity;
        }else {
            price = this.calculatePriceForPriceLevels(baseItem, modifiersToCalculate, includedModifiersToCalculate) * this.state.quantity;
        }
        return price;
    }
}
